import React, { useRef, useEffect } from 'react';
import { TextAreaField as PloneTextAreaField } from '@plone/components';
import { clsx } from 'clsx';
import './TextAreaField.scss';

export interface ESSTextAreaFieldProps {
  className?: string;
  /** Label for the text area field */
  label?: string;
  /** Placeholder text for the textarea */
  placeholder?: string;
  /** Helper text to display below the textarea */
  helperText?: string;
  /** Whether the textarea is in an error state */
  isInvalid?: boolean;
  /** Error message to display when isInvalid is true */
  errorMessage?: string;
  /** Number of visible text rows */
  rows?: number;
  /** Size of the text area field */
  size?: 'sm' | 'md' | 'lg';
  /** Field name */
  name?: string;
  /** Field value */
  value?: string;
  /** Change handler - receives the new value directly */
  onChange?: (value: string) => void;
  /** Whether the field is disabled */
  isDisabled?: boolean;
  /** Allow any other props */
  [key: string]: any;
}

export const TextAreaField: React.FC<ESSTextAreaFieldProps> = ({
  label,
  placeholder,
  helperText,
  isInvalid,
  errorMessage,
  size = 'md',
  className,
  onChange,
  rows,
  ...props
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (wrapperRef.current && rows) {
      const textarea = wrapperRef.current.querySelector('textarea');
      if (textarea) {
        textarea.setAttribute('rows', String(rows));
      }
    }
  }, [rows]);

  // Wrap onChange to handle both @plone/components API (value: string) and standard React events
  const handleChange = onChange
    ? (value: string) => {
        onChange(value);
      }
    : undefined;
  const fieldClasses = clsx(
    'ess-textareafield',
    `ess-textareafield--${size}`,
    {
      'ess-textareafield--invalid': isInvalid,
      'ess-textareafield--no-label': !label,
    },
    className,
  );

  return (
    <div
      ref={wrapperRef}
      className={fieldClasses}
      style={{ width: '100%', maxWidth: '100%', display: 'block' }}
    >
      <PloneTextAreaField
        label={label}
        placeholder={placeholder}
        isInvalid={isInvalid}
        onChange={handleChange}
        className="ess-textareafield__field"
        style={{ width: '100%', maxWidth: '100%' }}
        {...props}
      />
      {helperText && !isInvalid && (
        <div className="ess-textareafield__help-text">{helperText}</div>
      )}

      {isInvalid && errorMessage && (
        <div className="ess-textfield__error-text" role="alert">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

TextAreaField.displayName = 'ESSTextAreaField';
